/**
 * The item shop: a featured rotation, a daily rotation, and what they cost.
 *
 * ===========================================================================
 * THE ROTATION IS A PURE FUNCTION OF THE DAY
 * ===========================================================================
 *
 * ./cosmetics states the rule and the reason, and this file is where it is
 * kept: the rotation for a given calendar day is `rollShop(day)` and nothing
 * else. No `Math.random`, no wall clock, no stored roll. A shop that re-rolled
 * on refresh would be one a player rerolls by reloading the tab, and it could
 * not be tested at all — "yesterday's shop" has to be a value you can ask for,
 * not a thing that has gone.
 *
 * The day comes in as days since the Unix epoch, from `shopDay(nowMs)`. The
 * caller supplies the clock; nothing here reads one.
 *
 * ===========================================================================
 * ONE RARITY LADDER, NOT TWO
 * ===========================================================================
 *
 * The shop price is `blueprintCost` (./drops) times `SHOP_MARKUP`. It is
 * deliberately NOT a second table of Credits per rarity: two ladders is two
 * things to keep in step, and the first time they drift a Very Rare costs more
 * in the shop than an Import. Deriving it also makes the one real invariant
 * free — buying an item outright always costs more than building a blueprint
 * you already earned, at every rarity and every paint, which is the relationship
 * the real game has.
 *
 * ===========================================================================
 * WHAT IS NOT HERE
 * ===========================================================================
 *
 * Blueprints and trade-in are in ./inventory (`buildBlueprint`, `tradeIn`,
 * `tradeInProblem`), which owns everything that adds to or removes from an
 * inventory. The shop screen calls them directly. A second implementation here
 * would be a second set of rules for destroying five of somebody's items.
 *
 * And there is no purchase path: Credits are earned, see the header of
 * ./cosmetics. `buyShopEntry` moves Credits the player already has, and the
 * only sources are the match award, the pass and the challenges.
 */

import { Rng } from '../physics/rng';
import { blueprintCost } from './drops';
import { grantItem, ownsItem } from './inventory';
import { ITEMS, itemById } from './items';
import {
  PAINT_COLORS,
  SHOP_DAILY_COUNT,
  SHOP_FEATURED_COUNT,
  type ItemDef,
  type PaintColor,
  type PlayerProfile,
  type Rarity,
  type ShopEntry,
  type ShopRotation,
} from './cosmetics';

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

export const DAY_MS = 86_400_000;

/** Days since the Unix epoch, UTC. The shop's only notion of time. */
export const shopDay = (nowMs: number): number => Math.floor(nowMs / DAY_MS);

/** When the day-`day` rotation is replaced, in epoch ms. */
export const shopRefreshAt = (day: number): number => (day + 1) * DAY_MS;

/**
 * The build's fixed shop seed. Changing it reshuffles every past and future
 * rotation, which is why it is a named constant and not a literal at a call
 * site: it is a value somebody has to decide to change.
 */
export const SHOP_SEED = 0x5203_0b17;

// ---------------------------------------------------------------------------
// Prices
// ---------------------------------------------------------------------------

/** What the shop charges over the same item's blueprint build cost. */
export const SHOP_MARKUP = 1.6;

/** The price of one catalogue item, painted or not. */
export const priceFor = (def: ItemDef, paint?: PaintColor): number =>
  Math.round(blueprintCost(def, paint) * SHOP_MARKUP);

/** The price of an id, or null when the catalogue does not have it. */
export const priceForId = (itemId: string, paint?: PaintColor): number | null => {
  const def = itemById(itemId);
  return def === undefined ? null : priceFor(def, paint);
};

/** The discounts a featured slot can carry, as fractions off. */
const SALE_FRACTIONS: readonly number[] = [0.2, 0.3, 0.5];

/** Chance a featured slot is on sale, and that it is painted. */
const FEATURED_SALE_CHANCE = 0.35;
const FEATURED_PAINT_CHANCE = 0.4;
/** The daily row is the cheap row: painted far less often, never on sale. */
const DAILY_PAINT_CHANCE = 0.15;

// ---------------------------------------------------------------------------
// Rotation
// ---------------------------------------------------------------------------

/**
 * Which rarities each row draws from.
 *
 * Featured is the expensive row and daily the everyday one, which is the split
 * the real shop has. `limited` is in neither: it is the event tier, and an
 * event item that can be bought on any given Tuesday is not a limited item.
 */
const FEATURED_RARITIES: readonly Rarity[] = ['veryRare', 'import', 'exotic', 'blackMarket'];
const DAILY_RARITIES: readonly Rarity[] = ['common', 'uncommon', 'rare', 'veryRare'];

/**
 * Mix a seed, a day and a row salt into a generator state.
 *
 * The multiply is not decoration: mulberry32 advances its state by a fixed odd
 * constant, so seeding it with `seed + day` would make tomorrow's first draw
 * exactly one step from today's and consecutive days would rhyme.
 */
const rngFor = (seed: number, day: number, salt: number): Rng =>
  new Rng((Math.imul(day + salt, 0x9e37_79b1) ^ seed) >>> 0);

/** The catalogue entries a row may draw, in catalogue order. */
const poolFor = (catalogue: readonly ItemDef[], rarities: readonly Rarity[]): ItemDef[] =>
  catalogue.filter(item => !item.exclusive && rarities.includes(item.rarity));

/**
 * Draw `count` distinct items from `pool` without replacement.
 *
 * A partial Fisher-Yates over a copy: with a pool smaller than `count` it
 * returns the whole pool, which is the right answer for a filtered catalogue
 * and is a state the UI has to survive anyway.
 */
const drawDistinct = (pool: readonly ItemDef[], count: number, rng: Rng): ItemDef[] => {
  const bag = [...pool];
  const out: ItemDef[] = [];
  const wanted = Math.min(count, bag.length);
  for (let i = 0; i < wanted; i++) {
    const pick = i + rng.nextInt(bag.length - i);
    const chosen = bag[pick];
    bag[pick] = bag[i];
    bag[i] = chosen;
    out.push(chosen);
  }
  return out;
};

/**
 * Roll a paint for an item, or undefined.
 *
 * Titanium White is the last entry of `PAINT_COLORS` and the contract says it
 * should be the rarest roll rather than just another colour, so it is drawn
 * from the other twelve unless a second, narrower roll lands on it.
 */
const rollPaint = (item: ItemDef, chance: number, rng: Rng): PaintColor | undefined => {
  if (!item.paintable || rng.next() >= chance) return undefined;
  const rest = PAINT_COLORS.length - 1;
  if (rng.next() < 1 / (rest + 1)) return PAINT_COLORS[rest];
  return PAINT_COLORS[rng.nextInt(rest)];
};

const featuredEntry = (item: ItemDef, rng: Rng): ShopEntry => {
  const paint = rollPaint(item, FEATURED_PAINT_CHANCE, rng);
  const full = priceFor(item, paint);
  const painted = paint === undefined ? {} : { paint };
  if (rng.next() >= FEATURED_SALE_CHANCE) {
    return { itemId: item.id, ...painted, price: full };
  }
  const off = SALE_FRACTIONS[rng.nextInt(SALE_FRACTIONS.length)];
  return { itemId: item.id, ...painted, price: Math.round(full * (1 - off)), wasPrice: full };
};

const dailyEntry = (item: ItemDef, rng: Rng): ShopEntry => {
  const paint = rollPaint(item, DAILY_PAINT_CHANCE, rng);
  const painted = paint === undefined ? {} : { paint };
  return { itemId: item.id, ...painted, price: priceFor(item, paint) };
};

/**
 * The shop for one calendar day. Deterministic in `(day, catalogue, seed)` and
 * in nothing else.
 *
 * The two rows are rolled from separately salted generators, so a catalogue
 * change that alters the size of the featured pool cannot shift the daily row
 * as a side effect.
 */
export const rollShop = (
  day: number,
  catalogue: readonly ItemDef[] = ITEMS,
  seed: number = SHOP_SEED
): ShopRotation => {
  const featuredRng = rngFor(seed, day, 1);
  const dailyRng = rngFor(seed, day, 2);

  const featured = drawDistinct(
    poolFor(catalogue, FEATURED_RARITIES),
    SHOP_FEATURED_COUNT,
    featuredRng
  ).map(item => featuredEntry(item, featuredRng));

  const daily = drawDistinct(poolFor(catalogue, DAILY_RARITIES), SHOP_DAILY_COUNT, dailyRng).map(
    item => dailyEntry(item, dailyRng)
  );

  return { day, featured, daily };
};

// ---------------------------------------------------------------------------
// Buying
// ---------------------------------------------------------------------------

export type PurchaseFailure =
  /** No such id in the catalogue — a stale rotation, or a hand-edited store. */
  | 'unknown-item'
  /** The player already has this exact item and paint. */
  | 'owned'
  | 'insufficient-credits';

export interface PurchaseResult {
  ok: boolean;
  reason?: PurchaseFailure;
  /** Credits actually spent. Zero on every failure. */
  spent: number;
  /** The profile after the purchase — the same one back on a refusal. */
  profile: PlayerProfile;
}

/**
 * Buy one shop entry.
 *
 * Every refusal is named, because "nothing happened" on a purchase is the one
 * outcome a player will assume is a bug. Nothing is spent unless the grant
 * happens, and the order below is what guarantees it: all three checks, then
 * the debit and the grant together.
 *
 * `entry.price` is authoritative rather than being recomputed, so a rotation
 * with a sale on it charges the sale price and not the full one.
 */
export const buyShopEntry = (profile: PlayerProfile, entry: ShopEntry): PurchaseResult => {
  if (itemById(entry.itemId) === undefined) {
    return { ok: false, reason: 'unknown-item', spent: 0, profile };
  }
  if (ownsItem(profile, entry.itemId, entry.paint)) {
    return { ok: false, reason: 'owned', spent: 0, profile };
  }
  if (profile.wallet.credits < entry.price) {
    return { ok: false, reason: 'insufficient-credits', spent: 0, profile };
  }

  const next = grantItem(profile, entry.itemId, entry.paint === undefined ? {} : { paint: entry.paint });
  next.wallet.credits -= entry.price;
  return { ok: true, spent: entry.price, profile: next };
};

/** Why a row cannot be bought right now, or null. Drives the shop screen. */
export const purchaseProblem = (
  profile: PlayerProfile,
  entry: ShopEntry
): PurchaseFailure | null => {
  if (itemById(entry.itemId) === undefined) return 'unknown-item';
  if (ownsItem(profile, entry.itemId, entry.paint)) return 'owned';
  if (profile.wallet.credits < entry.price) return 'insufficient-credits';
  return null;
};
